import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, Clock, PoundSterling, Info, X, Mail, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import api, { apiError } from "@/lib/api";

const TIME_SAVED = 0.75; // 75% average reduction in manual takeoff time
const MARGIN_PER_BID = 600; // conservative avg margin won per extra bid
const ANNUAL_COST = 348; // £29/mo billed monthly

const PRESETS = [
  { key: "drylining", label: "Drylining", takeoffs: 12, hours: 7, rate: 45, bids: 1 },
  { key: "electrical", label: "Electrical", takeoffs: 15, hours: 5, rate: 50, bids: 2 },
  { key: "joinery", label: "Joinery", takeoffs: 8, hours: 6, rate: 40, bids: 1 },
  { key: "plumbing", label: "Plumbing", takeoffs: 10, hours: 6, rate: 48, bids: 1 },
  { key: "groundworks", label: "Groundworks", takeoffs: 6, hours: 10, rate: 55, bids: 1 },
];

const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Math.round(n));

const Field = ({ label, testid, value, display, unit, min, max, step, onChange }) => (
  <div>
    <div className="flex items-baseline justify-between mb-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid={`${testid}-value`}>
        {display ?? value}{unit ? ` ${unit}` : ""}
      </span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} data-testid={testid} />
  </div>
);

const RoiCalculator = () => {
  const navigate = useNavigate();
  const [takeoffs, setTakeoffs] = useState(8);
  const [hours, setHours] = useState(6);
  const [rate, setRate] = useState(45);
  const [bids, setBids] = useState(1);
  const [activePreset, setActivePreset] = useState(null);
  const [showMethod, setShowMethod] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | sending | sent | error
  const [emailError, setEmailError] = useState("");

  const sendSummary = async () => {
    if (!email.trim()) { setEmailError("Enter your email"); return; }
    setEmailStatus("sending"); setEmailError("");
    try {
      await api.post("/roi/lead", { email: email.trim(), takeoffs, hours, rate, bids, origin_url: window.location.origin });
      setEmailStatus("sent");
    } catch (err) {
      setEmailStatus("error");
      setEmailError(apiError(err.response?.data?.detail) || "Couldn't send — please check the email and try again.");
    }
  };

  const applyPreset = (p) => {
    setTakeoffs(p.takeoffs); setHours(p.hours); setRate(p.rate); setBids(p.bids); setActivePreset(p.key);
  };

  const r = useMemo(() => {
    const hoursSavedMonthly = takeoffs * hours * TIME_SAVED;
    const hoursSavedAnnually = hoursSavedMonthly * 12;
    const laborSavedMonthly = hoursSavedMonthly * rate;
    const laborSavedAnnually = laborSavedMonthly * 12;
    const extraRevenueAnnually = bids * 12 * MARGIN_PER_BID;
    const netBenefitAnnually = laborSavedAnnually + extraRevenueAnnually - ANNUAL_COST;
    const payoff = Math.round((laborSavedAnnually + extraRevenueAnnually) / ANNUAL_COST);
    return { hoursSavedMonthly, hoursSavedAnnually, laborSavedMonthly, laborSavedAnnually, extraRevenueAnnually, netBenefitAnnually, payoff };
  }, [takeoffs, hours, rate, bids]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden max-w-5xl mx-auto" data-testid="roi-calculator-container">
      <div className="grid md:grid-cols-12">
        {/* Inputs */}
        <div className="p-6 md:p-8 bg-slate-50 md:border-r border-slate-200 space-y-6 md:col-span-7">
          <div>
            <h3 className="font-display text-xl font-semibold text-slate-900">What could TradeOff save you?</h3>
            <p className="mt-1 text-sm text-slate-500">Tap your trade for a starting point, then adjust to your numbers.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                data-testid={`roi-preset-${p.key}`}
                className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${activePreset === p.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="space-y-5">
            <Field label="Estimates / takeoffs per month" testid="roi-slider-takeoffs" value={takeoffs} unit="/mo" min={1} max={50} step={1} onChange={(v) => { setTakeoffs(v); setActivePreset(null); }} />
            <Field label="Average hours per takeoff today" testid="roi-slider-hours" value={hours} unit="hrs" min={1} max={20} step={0.5} onChange={(v) => { setHours(v); setActivePreset(null); }} />
            <Field label="Your hourly rate (or day rate ÷ 8)" testid="roi-slider-rate" value={rate} display={`£${rate}`} unit="/hr" min={20} max={100} step={5} onChange={(v) => { setRate(v); setActivePreset(null); }} />
            <Field label="Extra bids you'd take on with the time saved" testid="roi-slider-extra-bids" value={bids} unit="/mo" min={0} max={6} step={1} onChange={(v) => { setBids(v); setActivePreset(null); }} />
          </div>

          <button onClick={() => setShowMethod(true)} data-testid="roi-methodology-toggle-btn" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2">
            <Info className="h-3.5 w-3.5" /> Show how we work this out
          </button>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 text-white space-y-6 flex flex-col justify-between md:col-span-5" style={{ backgroundColor: "#16305C" }}>
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-[0.15em]">
              <TrendingUp className="h-4 w-4" /> Your estimated return
            </div>

            <motion.div key={r.hoursSavedMonthly} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }}>
              <div className="text-sm text-slate-300 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Time saved</div>
              <div className="mt-1 text-3xl font-bold tabular-nums" data-testid="roi-output-hours-saved-monthly">{Math.round(r.hoursSavedMonthly)} hrs<span className="text-base font-medium text-slate-300">/mo</span></div>
              <div className="text-sm text-slate-400 tabular-nums" data-testid="roi-output-hours-saved-annually">≈ {Math.round(r.hoursSavedAnnually)} hrs a year back on the tools</div>
            </motion.div>

            <div className="h-px bg-white/10" />

            <div>
              <div className="text-sm text-slate-300 flex items-center gap-1.5"><PoundSterling className="h-4 w-4" /> Labour saved</div>
              <div className="mt-1 text-2xl font-bold tabular-nums" data-testid="roi-output-money-saved-annually">{gbp(r.laborSavedAnnually)}<span className="text-base font-medium text-slate-300">/yr</span></div>
              <div className="text-sm text-slate-400 tabular-nums" data-testid="roi-output-money-saved-monthly">{gbp(r.laborSavedMonthly)} per month</div>
            </div>

            {r.extraRevenueAnnually > 0 && (
              <div>
                <div className="text-sm text-slate-300">Extra revenue from more bids</div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-300" data-testid="roi-output-extra-revenue">{gbp(r.extraRevenueAnnually)}<span className="text-base font-medium text-slate-300">/yr</span></div>
              </div>
            )}

            <div className="rounded-lg bg-white/10 p-4">
              <div className="text-sm text-slate-200">Net benefit a year, after TradeOff (£29/mo)</div>
              <div className="mt-1 text-3xl font-bold tabular-nums text-white" data-testid="roi-output-net-benefit">{gbp(r.netBenefitAnnually)}</div>
              <div className="mt-1 text-xs text-blue-200 tabular-nums" data-testid="roi-output-payoff-ratio">That's about {r.payoff}× what TradeOff costs.</div>
            </div>
          </div>

          <div>
            <Button onClick={() => navigate("/register")} data-testid="roi-start-trial-cta-btn" className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-base">
              Start free — 1 project free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="mt-2 text-center text-xs text-slate-400">No credit card · Full features · 60 seconds</p>

            {emailStatus === "sent" ? (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-300" data-testid="roi-email-sent">
                <Check className="h-4 w-4" /> Sent — check your inbox for the summary.
              </div>
            ) : !emailOpen ? (
              <button onClick={() => setEmailOpen(true)} data-testid="roi-email-toggle-btn" className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors">
                <Mail className="h-4 w-4" /> Not ready? Email me this summary
              </button>
            ) : (
              <div className="mt-4 space-y-2" data-testid="roi-email-form">
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (emailStatus === "error") setEmailStatus("idle"); }}
                    placeholder="you@company.co.uk"
                    data-testid="roi-email-input"
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-10"
                    onKeyDown={(e) => { if (e.key === "Enter") sendSummary(); }}
                  />
                  <Button onClick={sendSummary} disabled={emailStatus === "sending"} data-testid="roi-email-submit-btn" className="h-10 bg-white text-slate-900 hover:bg-slate-100 shrink-0">
                    {emailStatus === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                  </Button>
                </div>
                {emailError && <p className="text-xs text-red-300" data-testid="roi-email-error">{emailError}</p>}
                <p className="text-[11px] text-slate-400">We'll email your saving summary and occasional TradeOff tips. Unsubscribe anytime.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 md:px-8 py-3 bg-slate-100 border-t border-slate-200 text-xs text-slate-500">
        Honest maths: based on a ~75% average reduction in takeoff time reported by UK trade users. Adjust the sliders to your own numbers.
      </div>

      {showMethod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowMethod(false)} data-testid="roi-methodology-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display text-lg font-semibold text-slate-900">How we work it out</h4>
              <button onClick={() => setShowMethod(false)} className="text-slate-400 hover:text-slate-700" data-testid="roi-methodology-close"><X className="h-5 w-5" /></button>
            </div>
            <ul className="text-sm text-slate-600 space-y-2 leading-relaxed">
              <li>• <strong>Time saved</strong> = takeoffs × hours each × 75% (typical AI takeoff time saving).</li>
              <li>• <strong>Labour saved</strong> = time saved × your hourly rate.</li>
              <li>• <strong>Extra revenue</strong> = extra bids × 12 months × {gbp(MARGIN_PER_BID)} assumed margin per won bid.</li>
              <li>• <strong>Net benefit</strong> = labour saved + extra revenue − {gbp(ANNUAL_COST)} (TradeOff a year).</li>
            </ul>
            <p className="text-xs text-slate-400">These are estimates to help you gauge value — your mileage will vary with drawing quality and how much you review.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoiCalculator;
